import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import styled from 'styled-components/macro';
import { theme } from 'lib/style';
import { NewModal } from 'lib/components';
import { VideoTag } from 'lib/components';
import { useAuth } from 'lib/context';
import { useGetDrop } from 'lib/api/droplr/getDrop';
import { useGetBoards } from 'lib/api/droplr/getBoards';
import { CombinedBoard } from 'lib/api/droplr/types';
import { useEditDrop } from 'lib/api/droplr/editDrop';
import { droplrModalsSelectStyle } from 'lib/style/commonStyles';
import { useIsMutating } from 'react-query';
import { droplrKeys } from 'lib/api/droplr/queryKeys';
import { Button } from 'react-covideo-common';

const SelectInput = styled(Select)`
  position: relative;
  display: block;
  width: 528px;
  font-size: 16px;
  height: 40px;
  box-sizing: border-box;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  border: none;
  box-sizing: border-box;
  &:focus {
    color: #495057;
    background-color: ${theme.palette.white};
    outline: 0;
  }
`;

const BoardsContainer = styled.div`
  height: 250px;
  display: flex;
  flex-direction: row;
  margin-top: 18px;
  flex-flow: row wrap;
  align-content: flex-start;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

type Props = {
  dropIds: string[];
  handleModalClose: () => void;
};

type Option = {
  label: string;
  value: string;
};

export const SetBoardsModal = ({ handleModalClose, dropIds }: Props) => {
  const [inputValue, onInputChange] = useState('');
  const [selectedValues, setSelectedValues] = useState([] as Option[]);

  const { userData } = useAuth();

  const isSingularDrop = dropIds.length === 1;

  const { data: drop, refetch: refetchDrop } = useGetDrop({
    dropId: isSingularDrop ? dropIds[0] : '',
  });

  const { data: boardsResult, refetch: refetchBoards } = useGetBoards({
    userId: Number(userData.id),
    customerId: Number(userData.customerId),
  });

  // board options
  const boards = (boardsResult?.boards || [])
    .filter((board: CombinedBoard) => board.userStatus !== 'readCollaborator')
    .map((board: CombinedBoard) => ({
      label: board.name,
      value: board.id,
    }));

  const { mutateAsync: editDrop } = useEditDrop();
  const isLoading =
    useIsMutating({
      mutationKey: droplrKeys.edit_drop_mutation(),
    }) > 0;

  useEffect(() => {
    if (isSingularDrop && drop?.boards && drop.boards.length) {
      // get board names for chosen drop
      const dropBoards = (boardsResult?.boards || []).filter(
        (board: CombinedBoard) => (drop.boards || []).includes(board.id)
      );
      setSelectedValues(
        dropBoards.map(dropBoard => ({
          label: dropBoard.name,
          value: dropBoard.id,
        }))
      );
    }
  }, [drop]);

  const removeBoard = (board: Option) => {
    setSelectedValues(
      selectedValues.filter((b: Option) => b.value !== board.value)
    );
  };

  const onChange = (newBoards: Option[]) => {
    setSelectedValues(newBoards);
  };

  const updateBoards = async () => {
    // add all selected boards to all selected drops
    for (let dropId of dropIds) {
      await editDrop({
        dropId,
        boards: selectedValues.map(board => board.value),
      });
    }
    refetchDrop();
    refetchBoards();
  };

  return (
    <NewModal
      closeModal={handleModalClose}
      headerText='Manage Boards'
      style={{
        content: {
          width: '528px',
          minWidth: '528px',
          height: '400px',
        },
      }}
    >
      <div>
        <SelectInput
          inputValue={inputValue}
          onInputChange={onInputChange}
          styles={droplrModalsSelectStyle}
          placeholder={'Add boards(s)...'}
          value={selectedValues}
          onChange={onChange}
          options={boards}
          noOptionsMessage={() => null}
          isMulti={true}
          clearable={false}
          optionHeight={40}
        />

        <BoardsContainer>
          {selectedValues &&
            !!selectedValues.length &&
            selectedValues.map((board: Option) => (
              <VideoTag
                key={board.value}
                tag={board}
                showRemove={true}
                onRemove={removeBoard}
              />
            ))}
        </BoardsContainer>

        <Footer>
          <Button
            onClick={async () => {
              await updateBoards();
              handleModalClose();
            }}
            text='Save'
            type='submit'
            disabled={isLoading}
          />
        </Footer>
      </div>
    </NewModal>
  );
};
